'use client';

// @boty/ui - useToast hook
// Hook to access toast notification functions

import { useContext } from 'react';
import { ToastContext, type ToastContextValue } from '../components/Toast';

/**
 * useToast - Access toast notification functions
 *
 * Must be used within a ToastProvider component.
 *
 * @returns Toast context with showToast, success, error, warning, info methods
 * @throws Error if used outside ToastProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const toast = useToast();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       toast.success('Saved successfully!');
 *     } catch (err) {
 *       toast.error('Failed to save');
 *     }
 *   };
 *
 *   return <Button onClick={handleSave}>Save</Button>;
 * }
 * ```
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (context === null) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}
